/**
 * CreditsListPage
 * Lists all credits for the partner with search, filters, and click-to-detail navigation
 * Route: /credit/active (replaces ComingSoon)
 */

import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

import { CreditLevel } from '@/types/credit.types';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';
import { CREDIT_STATUSES, CREDIT_LEVELS } from '@/types/backoffice-crud.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Portafolio', path: ROUTES.PORTFOLIO_DASHBOARD },
  { label: 'Creditos', path: ROUTES.ACTIVE_CREDITS },
];

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DELINQUENT':
      return 'warning';
    case 'DEFAULT':
      return 'danger';
    case 'PAID_OFF':
      return 'info';
    case 'CANCELLED':
    case 'RESTRUCTURED':
      return 'default';
    default:
      return 'default';
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

// ─── Columns ──────────────────────────────────────────────────────────────────

const columns: ColumnDef<BackofficeCreditsListItem, unknown>[] = [
  {
    accessorKey: 'disbursed_at',
    header: 'Desembolso',
    cell: ({ getValue }) => formatDateShort(getValue<string>()),
  },
  {
    accessorKey: 'full_name',
    header: 'Nombre',
    cell: ({ getValue }) => {
      const name = getValue<string | null | undefined>();
      return name ? (
        <span>{name}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      );
    },
  },
  {
    accessorKey: 'phone_number',
    header: 'Telefono',
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue<string>()}</span>
    ),
  },
  {
    accessorKey: 'credit_level',
    header: 'Nivel',
    cell: ({ getValue }) => {
      const level = getValue<string>();
      const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
      return isValid ? (
        <CreditLevelBadge level={level as CreditLevel} size="sm" />
      ) : (
        <span className="text-xs text-muted-foreground">{level}</span>
      );
    },
  },
  {
    accessorKey: 'amount_mxn',
    header: 'Monto',
    cell: ({ getValue }) => (
      <span className="tabular-nums">
        {formatCurrency(getValue<number>(), 'MXN', 'es-MX')}
      </span>
    ),
  },
  {
    accessorKey: 'term_weeks',
    header: 'Plazo',
    cell: ({ getValue }) => `${getValue<number>()} semanas`,
  },
  {
    accessorKey: 'weekly_payment_mxn',
    header: 'Pago Semanal',
    cell: ({ getValue }) => (
      <span className="tabular-nums">
        {formatCurrency(getValue<number>(), 'MXN', 'es-MX')}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ getValue }) => {
      const status = getValue<string>();
      return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
    },
  },
  {
    accessorKey: 'credit_id',
    header: 'ID',
    cell: ({ getValue }) => (
      <span className="font-mono text-xs" title={getValue<string>()}>
        {truncateId(getValue<string>())}
      </span>
    ),
  },
];

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ListSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Skeleton variant="rectangular" width="320px" height={40} />
        <Skeleton variant="rectangular" width="160px" height={40} />
        <Skeleton variant="rectangular" width="160px" height={40} />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={`row-skeleton-${i.toString()}`} variant="rectangular" height={44} />
        ))}
      </div>
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function ListError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar los creditos
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function CreditsListPage() {
  const { credits, isLoading, isError, refetch } = useBackofficeCredits();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState(searchParams.get('level') ?? '');

  const filteredCredits = useMemo(() => {
    let result = credits;
    if (statusFilter) {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (levelFilter) {
      result = result.filter((c) => c.credit_level === levelFilter);
    }
    return result;
  }, [credits, statusFilter, levelFilter]);

  const statusOptions = [
    { value: '', label: 'Todos los estados' },
    ...CREDIT_STATUSES.map((s) => ({ value: s, label: s })),
  ];

  const levelOptions = [
    { value: '', label: 'Todos los niveles' },
    ...CREDIT_LEVELS.map((l) => ({ value: l, label: l })),
  ];

  const handleRowClick = (row: BackofficeCreditsListItem) => {
    navigate(`/credit/detail/${row.credit_id}?directoryId=${row.directory_id}`);
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Creditos" helpKey="credits-list"
          subtitle="Lista de creditos del portafolio"
          breadcrumbs={BREADCRUMBS}
        />
        <ListSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader
          title="Creditos" helpKey="credits-list"
          subtitle="Lista de creditos del portafolio"
          breadcrumbs={BREADCRUMBS}
        />
        <ListError onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Creditos" helpKey="credits-list"
        subtitle="Lista de creditos del portafolio"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Filtrar por estado"
        />
        <Select
          options={levelOptions}
          value={levelFilter}
          onChange={setLevelFilter}
          placeholder="Filtrar por nivel"
        />
      </div>

      <DataTable
        data={filteredCredits}
        columns={columns}
        searchPlaceholder="Buscar por telefono, nombre o ID de credito..."
        onRowClick={handleRowClick}
        emptyMessage="No se encontraron creditos"
        pageSize={10}
        initialSorting={[{ id: 'disbursed_at', desc: true }]}
      />
    </div>
  );
}
